import axios from "axios";
import { Form, Popup } from "devextreme-react";
import { Item, Label, RequiredRule } from "devextreme-react/form";
import { ToolbarItem } from "devextreme-react/popup";
import { useEffect, useRef, useState } from "react";
import { billingTiming, contractType, projectStatus } from "../../data";
import { useSelector } from "react-redux";
import PTCPolicyGrid from "./PTCPolicyGrid";
import styled from "styled-components";

const ContractInfoWrapper = styled.div`
  .dx-field-item-required-mark {
    color: red;
  }

  .dx-datebox-calendar .dx-dropdowneditor-icon::before {
    content: "\f026";
  }
`;

export default function ContractInfo(props) {
  // console.debug("[ContractInfo]");

  const {
    contractInfoPopupVisible,
    setContractInfoPopupVisible,
    contractBasicInfo,
    paymentStatusGridRef,
    projectStatusDataSource,
  } = props;

  // const contractBasicInfo = useSelector((state) => state.contractInfo);
  const [mode, setMode] = useState("read");
  const [contractInfo, setContractInfo] = useState({
    ...contractBasicInfo,
  });

  useEffect(async () => {
    if (contractBasicInfo) {
      const result = await axios.get(
        `${process.env.REACT_APP_BECCO_API_URL}/api/salesMgmt/payt/paytStatus/contrInfo/${contractBasicInfo.contrNo}?cat=${contractBasicInfo.cat}`
      );

      setContractInfo((prevState) => ({
        // ...prevState,
        ...contractBasicInfo,
        ...result.data,
        updateFields: [],
      }));
    }
  }, [contractBasicInfo]);

  const contractInfoFormRef = useRef(null);

  const onHiding = () => {
    setContractInfoPopupVisible(false);
    setMode("read");
  };

  const onEditClick = () => {
    setMode("update");
  };

  const onSaveClick = async () => {
    if (contractInfoFormRef.current.instance.validate().isValid) {
      setContractInfoPopupVisible(false);
      setMode("read");

      const updateValues = {};

      for (const key of contractInfo.updateFields) {
        if (contractInfo.hasOwnProperty(key)) {
          updateValues[key] = contractInfo[key];
        }
      }

      axios
        .put(
          `${process.env.REACT_APP_BECCO_API_URL}/api/salesMgmt/payt/paytStatus/contrInfo/${contractInfo.contrNo}`,
          updateValues
        )
        .then(() => {
          paymentStatusGridRef.current.instance.refresh();
        });
    }
  };

  const onValueChanged = (e) => {
    setContractInfo((prevState) => ({
      ...prevState,
      updateFields: [...prevState.updateFields, e.element.id],
    }));
  };

  return (
    <Popup
      visible={contractInfoPopupVisible}
      dragEnabled={false}
      hideOnOutsideClick={false}
      showCloseButton={true}
      showTitle={true}
      title="Contract Info."
      height={"auto"}
      onHiding={onHiding}
    >
      {mode == "read" && (
        <ToolbarItem
          widget={"dxButton"}
          toolbar={"top"}
          location={"after"}
          options={{
            icon: "edit",
            type: "normal",
            stylingMode: "contained",
            onClick: onEditClick,
          }}
        />
      )}
      {mode == "update" && (
        <ToolbarItem
          widget={"dxButton"}
          toolbar={"top"}
          location={"after"}
          options={{
            icon: "save",
            type: "normal",
            stylingMode: "contained",
            onClick: onSaveClick,
          }}
        />
      )}
      {contractInfo && contractInfo.cat == "1" && (
        <ContractInfoWrapper>
          <Form
            ref={contractInfoFormRef}
            formData={contractInfo}
            colCount={4}
            labelMode="outside"
            labelLocation="top"
            showColonAfterLabel={true}
            style={{
              padding: "16px",
            }}
            validationGroup="contractInfoData"
          >
            <Item
              dataField={"sprName"}
              editorOptions={{
                readOnly: true,
              }}
            >
              <Label text={"Company Name"} />
            </Item>
            <Item
              dataField={"contrType"}
              editorType={"dxSelectBox"}
              editorOptions={{
                readOnly: true,
                dataSource: contractType,
                valueExpr: "id",
                displayExpr: "name",
              }}
            >
              <Label text={"Contract Type"} />
            </Item>
            <Item
              dataField={"contrDate"}
              editorType={"dxDateBox"}
              editorOptions={{
                displayFormat: "yyyy-MM-dd",
                dateSerializationFormat: "yyyy-MM-dd",
                showCloseButton: true,
                useMaskBehavior: true,
                readOnly: mode == "read" ? true : false,
                name: "contrDate",
                elementAttr: {
                  id: "contrDate",
                },
                onValueChanged: onValueChanged,
              }}
            >
              <Label text={"Contract Date"} />
              <RequiredRule message={"Contract date is required."} />
            </Item>
            <Item
              dataField={"projStatusCodeId"}
              editorType={"dxSelectBox"}
              editorOptions={{
                // dataSource: projectStatusDataSource,
                dataSource: projectStatus,
                displayExpr: "name",
                valueExpr: "codeId",
                readOnly: mode == "read" ? true : false,
                elementAttr: {
                  id: "projStatusCodeId",
                },
                onValueChanged: onValueChanged,
              }}
            >
              <Label text={"Project Status"} />
              <RequiredRule message={"Project status is required."} />
            </Item>
            <Item
              dataField={"contrNo"}
              editorOptions={{
                readOnly: true,
              }}
            >
              <Label text={"Contract No."} />
            </Item>
            <Item
              dataField={"quotNo"}
              editorOptions={{
                readOnly: true,
              }}
            >
              <Label text={"Quotation No."} />
            </Item>
            <Item
              dataField={"protocolNo"}
              editorType={"dxTextBox"}
              editorOptions={{
                readOnly: mode == "read" ? true : false,
                elementAttr: {
                  id: "protocolNo",
                },
                onValueChanged: onValueChanged,
              }}
            >
              <Label text={"Protocol No."} />
              <RequiredRule message={"Protocol No. is required."} />
            </Item>
            <Item
              dataField={"po"}
              editorType={"dxTextBox"}
              editorOptions={{
                readOnly: mode == "read" ? true : false,
                elementAttr: {
                  id: "po",
                },
                onValueChanged: onValueChanged,
              }}
            >
              <Label text={"PO"} />
            </Item>
            <Item
              dataField={"name"}
              editorType={"dxTextBox"}
              colSpan={2}
              editorOptions={{
                readOnly: mode == "read" ? true : false,
                elementAttr: {
                  id: "name",
                },
                onValueChanged: onValueChanged,
              }}
            >
              <Label text={"Study Name"} />
            </Item>
            <Item
              dataField={"product"}
              editorType={"dxTextBox"}
              editorOptions={{
                readOnly: mode == "read" ? true : false,
                elementAttr: {
                  id: "product",
                },
                onValueChanged: onValueChanged,
              }}
            >
              <Label text={"Product/Device"} />
            </Item>
            <Item
              dataField={"ingredient"}
              editorType={"dxTextBox"}
              editorOptions={{
                readOnly: mode == "read" ? true : false,
                elementAttr: {
                  id: "ingredient",
                },
                onValueChanged: onValueChanged,
              }}
            >
              <Label text={"Ingredient"} />
            </Item>
            <Item
              dataField={"totalContrCost"}
              editorType={"dxNumberBox"}
              editorOptions={{
                readOnly: true,
                format: "#,###",
                inputAttr: {
                  style: "text-align : right",
                },
              }}
            >
              <Label text={"Total Contract Cost (Excl.VAT)"} />
            </Item>
          </Form>
        </ContractInfoWrapper>
      )}
      {contractInfo && contractInfo.cat == "2" && (
        <Form
          ref={contractInfoFormRef}
          formData={contractInfo}
          colCount={4}
          labelMode="outside"
          labelLocation="top"
          showColonAfterLabel={true}
          style={{
            padding: "16px",
          }}
          validationGroup="contractInfoData"
        >
          <Item dataField={"sprName"} editorOptions={{ readOnly: true }}>
            <Label text={"Company Name"} />
          </Item>
          <Item
            dataField={"contrNo"}
            editorOptions={{
              readOnly: true,
            }}
          >
            <Label text={"Contract No."} />
          </Item>
          <Item
            dataField={"AOSQuotNo"}
            editorOptions={{
              readOnly: true,
            }}
          >
            <Label text={"AOS Quotation No."} />
          </Item>
          <Item
            dataField={"protocolNo"}
            editorType={"dxTextBox"}
            editorOptions={{
              readOnly: mode == "read" ? true : false,
              elementAttr: {
                id: "protocolNo",
              },
              onValueChanged: onValueChanged,
            }}
          >
            <Label text={"Protocol No"} />
            <RequiredRule message={"Protocol No. is required."} />
          </Item>
          <Item
            dataField={"AOSBillingTiming"}
            editorType={"dxSelectBox"}
            editorOptions={{
              readOnly: mode == "read" ? true : false,
              dataSource: billingTiming,
              valueExpr: "id",
              displayExpr: "name",
              elementAttr: {
                id: "AOSBillingTiming",
              },
              onValueChanged: onValueChanged,
            }}
          >
            <Label text={"AOS 청구시기"} />
          </Item>
        </Form>
      )}
      {contractInfo && contractInfo.cat == "3" && (
        <>
          <Form
            ref={contractInfoFormRef}
            formData={contractInfo}
            colCount={4}
            labelMode="outside"
            labelLocation="top"
            showColonAfterLabel={true}
            style={{
              padding: "16px",
            }}
            validationGroup="contractInfoData"
          >
            <Item
              dataField={"sprName"}
              editorOptions={{
                readOnly: true,
              }}
            >
              <Label text={"Company Name"} />
            </Item>
            <Item
              dataField={"contrNo"}
              editorOptions={{
                readOnly: true,
              }}
            >
              <Label text={"Contract No."} />
            </Item>
            <Item
              dataField={"quotNo"}
              editorOptions={{
                readOnly: true,
              }}
            >
              <Label text={"Quotation No."} />
            </Item>
            <Item
              dataField={"protocolNo"}
              editorType={"dxTextBox"}
              editorOptions={{
                readOnly: mode == "read" ? true : false,
                elementAttr: {
                  id: "protocolNo",
                },
                onValueChanged: onValueChanged,
              }}
            >
              <Label text={"Protocol No."} />
              <RequiredRule message={"Protocol No. is required."} />
            </Item>
            <Item
              dataField={"PTCBillingTiming"}
              editorType={"dxSelectBox"}
              editorOptions={{
                readOnly: mode == "read" ? true : false,
                dataSource: billingTiming,
                valueExpr: "id",
                displayExpr: "name",
                elementAttr: {
                  id: "PTCBillingTiming",
                },
                onValueChanged: onValueChanged,
              }}
            >
              <Label text={"PTC 청구시기"} />
            </Item>
          </Form>
          <div>PTC Policy</div>
          <PTCPolicyGrid contrNo={contractBasicInfo.contrNo} mode={mode} />
        </>
      )}
    </Popup>
  );
}
